/** SUNTRONIX ONE — Preview Mode context.
 *
 * Provides state + actions for the "Preview As Customer" experience.
 * Polls /api/preview/current on mount and after every mutation so the
 * banner + shell always reflect what the server thinks is active.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const PreviewContext = createContext(null);

const DEVICE_PRESETS = {
  desktop:         { label: "Desktop",  width: null, height: null },
  laptop:          { label: "Laptop",   width: 1366, height: 768 },
  ipad_landscape:  { label: "iPad ⟷",   width: 1180, height: 820 },
  ipad_portrait:   { label: "iPad ↕",   width: 820,  height: 1180 },
  mobile:          { label: "Mobile",   width: 390,  height: 844 },
};

const ROLE_LABEL = {
  customer_global_admin: "Customer Admin",
  customer_it_admin:     "Customer IT",
  customer_finance:      "Customer Finance",
  customer_procurement:  "Customer Procurement",
  customer_hr:           "Customer HR",
  customer_viewer:       "Customer Viewer",
  customer_employee:     "Employee",
  master_client_admin:   "Master Client Admin",
};

export function PreviewProvider({ children }) {
  const [session, setSession] = useState(null);
  const [tenants, setTenants] = useState({ customers: [], master_clients: [] });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/preview/current");
      setSession(data.active || null);
    } catch { setSession(null); }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await api.get("/preview/tenants");
      setTenants(data);
    } catch { /* not super_admin — no tenants available */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const start = useCallback(async (masterCompanyId, opts = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post("/preview/start", {
        master_company_id: masterCompanyId,
        preview_role: opts.role || "customer_global_admin",
        device: opts.device || "desktop",
        reason: opts.reason || null,
        pre_activation: !!opts.pre_activation,
      });
      setSession(data.session);
      toast.success(`Previewing as ${data.session.tenant_name}`);
      // Reload so every screen re-fetches under the preview identity
      window.location.href = "/";
      return data.session;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start preview");
    } finally { setLoading(false); }
  }, []);

  const exit = useCallback(async () => {
    setLoading(true);
    try {
      await api.post("/preview/exit");
      setSession(null);
      toast.success("Exited preview mode");
      window.location.href = "/customers";
    } catch { toast.error("Failed to exit preview"); }
    finally { setLoading(false); }
  }, []);

  const switchRole = useCallback(async (role) => {
    try {
      const { data } = await api.post("/preview/switch-role", { preview_role: role });
      setSession(data.session);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch role");
    }
  }, []);

  const switchCustomer = useCallback(async (mcId) => {
    try {
      const { data } = await api.post("/preview/switch-customer", { master_company_id: mcId });
      setSession(data.session);
      window.location.href = "/";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch customer");
    }
  }, []);

  const switchDevice = useCallback(async (device) => {
    try {
      const { data } = await api.post("/preview/switch-device", { device });
      setSession(data.session);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch device");
    }
  }, []);

  return (
    <PreviewContext.Provider value={{
      session, active: !!session, loading, tenants,
      refresh, loadTenants, start, exit,
      switchRole, switchCustomer, switchDevice,
      DEVICE_PRESETS, ROLE_LABEL,
    }}>
      {children}
    </PreviewContext.Provider>
  );
}

export function usePreview() {
  const ctx = useContext(PreviewContext);
  if (!ctx) throw new Error("usePreview must be used inside PreviewProvider");
  return ctx;
}
